import { useState } from "react";
import { Button, Link, ScrollableSheet, SheetClose, Collapsible, CollapsibleContent, CollapsibleTrigger, TrustpilotStandardWidget as TrustpilotWidget } from "@shopmuchmore/design-system";
import {
  User,
  Home,
  ChevronRight,
  LogOut,
  Menu,
} from "lucide-react";
import { useAuthStore } from "../../features/auth/auth.store";
import { useProductCategoriesQuery } from "../../features/product/product.services";
import { featuredCategories } from "../../features/home/home.data";

interface NavigationDrawerProps {
  children?: React.ReactNode;
  className?: string; // For the default trigger icon styling
}

export function NavigationDrawer({ children, className }: NavigationDrawerProps) {
  const { customer, logout } = useAuthStore();
  const { data: categories } = useProductCategoriesQuery();
  const isAuthenticated = !!customer;
  const [open, setOpen] = useState(false);

  // Default trigger if no children provided
  // Ensure trigger is always a single element (button) with the provided className
  // If children are provided, they become the content of the button
  const trigger = (
    <Button variant="ghost" size="icon-sm" aria-label="Open menu" className={className}>
      {children || <Menu className="size-6" />}
    </Button>
  );

  const Header = (
    <div className="bg-secondary text-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-90">Browse</p>
          <p className="text-xl font-bold">ShopMuchMore</p>
        </div>
        <SheetClose asChild>
          <button className="p-1 hover:opacity-80 transition-opacity">
            <span className="sr-only">Close</span>
            <svg
              className="size-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </SheetClose>
      </div>

      {/* Sign In Section */}
      {!isAuthenticated ? (
        <Link
          href="/auth/login"
          variant="ghost"
          className="mt-4 flex items-center gap-2 text-primary-foreground hover:bg-primary-foreground/10 w-full justify-start"
          onClick={() => setOpen(false)}
        >
          <User className="size-5" />
          <span>Sign in</span>
        </Link>
      ) : (
        <div className="mt-4 flex items-center gap-2">
          <User className="size-5" />
          <span>Hello, {customer?.first_name}</span>
        </div>
      )}
    </div>
  );

  return (
    <ScrollableSheet
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      header={Header}
      side="left"
    >
      {/* Home Link */}
      <div className="border-b">
        <Link
          href="/"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-3 text-base font-medium hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>ShopMuchMore Home</span>
          <Home className="size-5 text-muted-foreground" />
        </Link>
      </div>

      {/* Trending Section */}
      <div className="border-b py-2">
        <p className="px-4 py-2 text-sm font-semibold text-foreground">
          Featured Categories
        </p>
        {featuredCategories.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            variant="ghost"
            className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
            onClick={() => setOpen(false)}
          >
            <span>{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Top Departments Section */}
      <div className="border-b py-2">
        <p className="px-4 py-2 text-sm font-semibold text-foreground">
          Top Departments
        </p>
        {categories?.map((category) => (
          <Collapsible key={category.id}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent focus:outline-none group">
                <span className="group-data-[state=open]:font-semibold">{category.name}</span>
                <ChevronRight className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-gray-50/50">
              {category.subcategory?.map((sub) => (
                <Link
                  key={sub.id}
                  href={`/browse?slug=${sub.slug}`}
                  variant="ghost"
                  className="flex items-center justify-between w-full pl-8 pr-4 py-2 text-[13px] text-gray-600 hover:text-primary hover:bg-accent"
                  onClick={() => setOpen(false)}
                >
                  <span>{sub.name}</span>
                </Link>
              ))}
              <Link
                href={`/browse?slug=${category.slug}`}
                variant="ghost"
                className="flex items-center justify-between w-full pl-8 pr-4 py-2 text-[13px] font-medium text-primary hover:bg-accent"
                onClick={() => setOpen(false)}
              >
                <span>View All {category.name}</span>
              </Link>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>

      {/* Account Section */}
      <div className="border-b py-2">
        <p className="px-4 py-2 text-sm font-semibold text-foreground">
          My Account
        </p>
        <Link
          href="/account"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>Account</span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
        <Link
          href="/account/orders"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>My Orders</span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
        <Link
          href="/account/wishlist"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>Wishlist</span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
      </div>

      {/* Help & Settings */}
      <div className="border-b py-2">
        <p className="px-4 py-2 text-sm font-semibold text-foreground">
          Help & Settings
        </p>
        <Link
          href="/about"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>About Us</span>
        </Link>
        <Link
          href="/contact"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>Contact Us</span>
        </Link>
        <Link
          href="/merchant-manual"
          variant="ghost"
          className="flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-accent"
          onClick={() => setOpen(false)}
        >
          <span>Sell on ShopMuchMore</span>
        </Link>
      </div>

      {/* Logout */}
      {isAuthenticated && (
        <div className="py-2">
          <button
            onClick={() => {
              logout();
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10"
          >
            <LogOut className="size-4" />
            Sign Out
          </button>
        </div>
      )}

      {/* Trustpilot Review Collector */}
      <div className="px-4 py-6 bg-gray-50/50 mt-auto border-t">
        <TrustpilotWidget isMobileOnly alignment="center" className="w-full" />
      </div>
    </ScrollableSheet>
  );
}
